import React from 'react';
import { compareDesc } from 'date-fns';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import {
  SmallTableExtraComponent,
  SmallTableRowComponent,
  SmallTableWrapperComponent,
} from 'design/smallTable';
import { TextComponent } from 'design/text/textComponents';
import { PartnerFinances } from 'types/state';
import {
  PartnerFinanceMonthReport,
  PartnerFinanceMonthRewardIncome,
} from 'types/partnerFinance';
import { DelimiterComponent } from 'design/delimiter';
import { MarginComponent } from 'design/margin';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';
import { ModalPressControlComponent } from 'design/modalPressControl';

import moneyBagImage from 'images/money-bag.png';

type Props = {
  partnerFinances: PartnerFinances;
  partnerFinanceMonthReports: PartnerFinanceMonthReport[];
  onShowClientDetails(id: number, organizationName: string): void;
  onCreateReward?(): void;
  onEditReward?(editedRewardIncome: PartnerFinanceMonthRewardIncome): void;
};

export const PartnerFinancesTable = (props: Props) => {
  const {
    partnerFinances,
    partnerFinanceMonthReports,
    onShowClientDetails,
    onCreateReward,
    onEditReward,
  } = props;

  const {
    partnerFinancesTable: ownDictionary,
    shared: sharedDictionary,
  } = useIntlDictionary();

  const sortedReports = React.useMemo(() => {
    return partnerFinanceMonthReports.sort((nextReport, prevReport) => {
      return compareDesc(
        new Date(nextReport.monthDateISO),
        new Date(prevReport.monthDateISO)
      );
    });
  }, [partnerFinanceMonthReports]);

  return (
    <>
      <SidebarModalSectionComponent
        content={
          <SmallTableWrapperComponent>
            <SmallTableRowComponent>
              <TextComponent size="mpp" weight="medium">
                {ownDictionary.balanceAmountLabel}
              </TextComponent>
              <TextComponent size="mpp" weight="medium">
                {sharedDictionary.moneyAmountText(
                  partnerFinances.balanceAmount || 0,
                  'USD'
                )}
              </TextComponent>
            </SmallTableRowComponent>
            <SmallTableRowComponent>
              <TextComponent>{ownDictionary.paidAmountLabel}</TextComponent>
              <TextComponent>
                {sharedDictionary.moneyAmountText(
                  partnerFinances.paidAmount || 0,
                  'USD'
                )}
              </TextComponent>
            </SmallTableRowComponent>
            <SmallTableRowComponent>
              <TextComponent>{ownDictionary.earnedAmountLabel}</TextComponent>
              <TextComponent>
                {sharedDictionary.moneyAmountText(
                  partnerFinances.earnedAmount || 0,
                  'USD'
                )}
              </TextComponent>
            </SmallTableRowComponent>
          </SmallTableWrapperComponent>
        }
        actions={
          onCreateReward ? (
            <ModalPressControlComponent
              theme="secondary"
              type="button"
              onPress={onCreateReward}
            >
              {ownDictionary.createRewardActionText}
            </ModalPressControlComponent>
          ) : null
        }
      />
      {sortedReports.map((partnerFinanceMonthReport) => (
        <SidebarModalSectionComponent
          lastSectionHasBorder
          content={
            <>
              <SmallTableWrapperComponent>
                <SmallTableRowComponent>
                  <TextComponent size="mpp" weight="medium">
                    {ownDictionary.periodMonthText(
                      partnerFinanceMonthReport.monthDateISO
                    )}
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.periodEarnedLabel}
                  </TextComponent>
                  <TextComponent>
                    {sharedDictionary.moneyAmountText(
                      partnerFinanceMonthReport.earnedAmount,
                      'USD'
                    )}
                  </TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>{ownDictionary.periodPaidLabel}</TextComponent>
                  <TextComponent>
                    {sharedDictionary.moneyAmountText(
                      partnerFinanceMonthReport.paidAmount,
                      'USD'
                    )}
                  </TextComponent>
                </SmallTableRowComponent>
              </SmallTableWrapperComponent>
              <MarginComponent top={28} bottom={28}>
                <DelimiterComponent />
              </MarginComponent>
              {partnerFinanceMonthReport.incomes.map((income, index, array) => (
                <React.Fragment key={income.dateISO}>
                  {income.type === 'BILL_INCOME' && (
                    <SmallTableWrapperComponent>
                      <SmallTableRowComponent>
                        <TextComponent>
                          {ownDictionary.transferMonthText(income.dateISO)}
                        </TextComponent>
                        <TextComponent>
                          <ClickControlComponent
                            onClick={() =>
                              onShowClientDetails(
                                income.clientId,
                                income.organization
                              )
                            }
                          >
                            <TextComponent color="accent">
                              {income.organization}
                            </TextComponent>
                          </ClickControlComponent>
                          <br />
                          <TextComponent color="primary">
                            {ownDictionary.transferPeriodText(
                              income.periodStartDateISO,
                              income.periodEndDateISO
                            )}
                          </TextComponent>
                        </TextComponent>
                      </SmallTableRowComponent>
                      <SmallTableExtraComponent>
                        <TextComponent color="successful">
                          {ownDictionary.transferIncomeText(income.amount)}
                        </TextComponent>
                      </SmallTableExtraComponent>
                    </SmallTableWrapperComponent>
                  )}
                  {income.type === 'REWARD_INCOME' && (
                    <SmallTableWrapperComponent>
                      <SmallTableRowComponent>
                        <TextComponent>
                          {ownDictionary.transferMonthText(income.dateISO)}
                        </TextComponent>
                        <TextComponent>
                          {ownDictionary.rewardLabelText}
                          <br />
                          {income.comment}
                          <br />
                          {onEditReward && (
                            <ClickControlComponent
                              onClick={() => onEditReward(income)}
                            >
                              <TextComponent color="accent">
                                {ownDictionary.editRewardActionText}
                              </TextComponent>
                            </ClickControlComponent>
                          )}
                        </TextComponent>
                      </SmallTableRowComponent>
                      <SmallTableExtraComponent>
                        <TextComponent color="primary">
                          <img
                            src={moneyBagImage}
                            alt="money bag"
                            style={{
                              display: 'inline',
                              width: '1em',
                            }}
                          />{' '}
                          {sharedDictionary.moneyAmountText(
                            income.amount,
                            'USD'
                          )}
                        </TextComponent>
                      </SmallTableExtraComponent>
                    </SmallTableWrapperComponent>
                  )}
                  {index < array.length - 1 && <MarginComponent bottom={24} />}
                </React.Fragment>
              ))}
            </>
          }
          actions={null}
        />
      ))}
    </>
  );
};
